import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Home,
  ChevronRight,
  ArrowUpRight,
  BadgePercent,
  Landmark,
  Ship,
  Wallet,
  Plane,
  Factory,
  CheckCircle2,
  Download,
  FileText,
  ChevronDown,
} from "lucide-react";

import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const heroBg = "/assets/images/bg2.jpeg";
const sectionBg = "/assets/images/bg2.jpeg";

const incentiveGroups = [
  {
    id: "customs",
    title: "Customs Incentives",
    icon: BadgePercent,
    items: [
      "No requirement for Form M when importing goods into the Free Zone.",
      "Fast-track cargo clearance for approved Free Zone operations.",
      "No time limit on cargo storage within the Free Zone.",
      "Exemption from customs duty within the Free Zone.",
      "Ability to store, process or re-export goods from the Free Zone.",
      "75% import duty rebate when goods are exported from the Free Zone into Nigerian territory.",
    ],
  },
  {
    id: "cashflow",
    title: "Cash-Flow Benefits",
    icon: Wallet,
    items: [
      "Deferred customs duties reduce project costs and improve investor cash flow.",
      "The zones create numerous investment opportunities across upstream and downstream oil and gas sectors.",
      "Investors can optimize capital deployment through Free Zone operational incentives.",
    ],
  },
  {
    id: "tax",
    title: "Tax & Financial Incentives",
    icon: Landmark,
    items: [
      "Free Zone enterprises are exempt from federal, state and local government taxes.",
      "100% foreign ownership is permitted.",
      "Investors may repatriate 100% of profit and capital without restrictions.",
    ],
  },
  {
    id: "immigration",
    title: "Immigration Incentives",
    icon: Plane,
    items: [
      "Expatriate employment is not subject to quotas.",
      "Work permits and expatriate quotas are fast-tracked.",
      "Immigrants can process permits through the Federal Government’s CERPAC portal.",
    ],
  },
  {
    id: "logistics",
    title: "Logistics & Supply Chain Incentives",
    icon: Ship,
    items: [
      "Waiver of pre-shipment inspection.",
      "Cargo movement is supported on a 24/7 basis.",
      "Direct sea and air access support efficient project execution.",
      "One-stop-shop operations reduce delays and improve service delivery.",
      "Support services are available to strengthen logistics and supply-chain efficiency.",
    ],
  },
  {
    id: "manufacturing",
    title: "Manufacturing & Processing Incentives",
    icon: Factory,
    items: [
      "Duty is payable only on raw materials for goods sold into Nigeria.",
      "Full duty applies on finished goods exported into Nigeria.",
      "Duty rebates and export allowances are available for goods exported from the Free Zone.",
    ],
  },
];

const highlights = [
  "100% foreign ownership permitted",
  "100% repatriation of profit and capital",
  "Exemption from federal, state and local taxes",
  "No customs duty within the Free Zone",
  "Fast-tracked cargo clearance",
  "24/7 cargo movement support",
];

const Incentives = () => {
  const pageRef = useRef(null);
  const [active, setActive] = useState("customs");

  const current =
    incentiveGroups.find((item) => item.id === active) || incentiveGroups[0];

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".hero-badge", {
        opacity: 0,
        y: 30,
        duration: 0.8,
        ease: "power3.out",
      });

      gsap.from(".hero-title", {
        opacity: 0,
        y: 80,
        duration: 1.1,
        ease: "power4.out",
      });

      gsap.from(".hero-text", {
        opacity: 0,
        y: 40,
        delay: 0.2,
        duration: 0.9,
        ease: "power3.out",
      });

      gsap.utils.toArray(".reveal-up").forEach((el) => {
        gsap.from(el, {
          opacity: 0,
          y: 50,
          duration: 0.8,
          ease: "power3.out",
          scrollTrigger: {
            trigger: el,
            start: "top 86%",
          },
        });
      });

      gsap.utils.toArray(".soft-card").forEach((el, index) => {
        gsap.from(el, {
          opacity: 0,
          y: 40,
          delay: index * 0.04,
          duration: 0.7,
          ease: "power3.out",
          scrollTrigger: {
            trigger: el,
            start: "top 92%",
          },
        });
      });

      gsap.to(".floating-orb-1", {
        y: 45,
        x: -25,
        duration: 7,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });

      gsap.to(".floating-orb-2", {
        y: -45,
        x: 25,
        duration: 8,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });
    }, pageRef);

    return () => ctx.revert();
  }, []);

  const CurrentIcon = current.icon;

  return (
    <main ref={pageRef} className="bg-white overflow-hidden">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden text-white">
        <img
          src={heroBg}
          alt="OGFZA Incentives"
          className="absolute inset-0 w-full h-full object-cover"
        />

        <div className="absolute inset-0 bg-[#041018]/75" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#082567]/85 via-[#082567]/55 to-[#026B07]/25" />

        <div className="floating-orb-1 absolute top-0 left-0 w-[520px] h-[520px] rounded-full bg-[#082567]/35 blur-3xl" />
        <div className="floating-orb-2 absolute bottom-0 right-0 w-[520px] h-[520px] rounded-full bg-[#F2AF11]/15 blur-3xl" />

        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.04)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.04)_1px,transparent_1px)] bg-[size:80px_80px]" />

        <div className="relative max-w-7xl mx-auto px-6 py-32">
          <div className="hero-badge inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/10 border border-white/10 backdrop-blur-md mb-8">
            <span className="w-2 h-2 rounded-full bg-[#F2AF11]" />
            Investor Benefits
          </div>

          <h1
            className="hero-title text-6xl md:text-8xl font-black leading-[0.95] max-w-5xl"
            style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
          >
            Incentives
          </h1>

          <p className="hero-text mt-8 max-w-3xl text-white/85 text-lg leading-[1.9]">
            OGFZA provides a competitive package of customs, tax, immigration,
            logistics, cash-flow and processing incentives designed to support
            investors operating within Nigeria’s Oil and Gas Free Zones.
          </p>

          <div className="hero-text flex items-center gap-2 mt-8 text-sm text-white/75">
            <Link
              to="/"
              className="flex items-center gap-1 hover:text-[#F2AF11] transition-colors"
            >
              <Home size={14} />
              Home
            </Link>

            <ChevronRight size={14} />

            <Link
              to="/about"
              className="hover:text-[#F2AF11] transition-colors"
            >
              About Us
            </Link>

            <ChevronRight size={14} />

            <span className="text-[#F2AF11]">Incentives</span>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="relative py-24 bg-white overflow-hidden">
        <div className="absolute top-0 right-0 w-[420px] h-[420px] rounded-full bg-[#F2AF11]/10 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[420px] h-[420px] rounded-full bg-[#026B07]/10 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-[0.9fr_1.1fr] gap-14 items-start">
          <div className="reveal-up">
            <p className="text-[#026B07] font-black tracking-[0.2em] uppercase text-xs mb-4">
              Incentive Framework
            </p>

            <h2
              className="text-4xl md:text-5xl font-black text-[#082567] leading-tight mb-6"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              A Free Zone Advantage Designed For Serious Investors
            </h2>

            <div className="w-24 h-1.5 bg-[#F2AF11] rounded-full mb-7" />

            <p className="text-slate-700 leading-[1.9] text-[15.5px]">
              The Oil and Gas Free Zones Authority offers incentives that help
              reduce operating costs, improve project cash flow, simplify cargo
              movement, support expatriate participation and strengthen investor
              confidence.
            </p>
          </div>

          <div className="reveal-up bg-white border border-slate-200 rounded-[32px] p-6 md:p-8 shadow-[0_24px_80px_-55px_rgba(8,37,103,0.55)]">
            <div className="grid sm:grid-cols-2 gap-4">
              {highlights.map((item, index) => (
                <div
                  key={index}
                  className="soft-card bg-[#FAFBFC] rounded-[22px] p-5 border border-slate-200 hover:border-[#F2AF11]/60 transition-all duration-500"
                >
                  <CheckCircle2 size={22} className="text-[#026B07] mb-4" />

                  <p className="text-[#082567] font-bold leading-[1.6] text-[15px]">
                    {item}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* INTERACTIVE INCENTIVE PANEL */}
      <section className="relative py-24 bg-[#FAFBFC] overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04] bg-[linear-gradient(to_right,#082567_1px,transparent_1px),linear-gradient(to_bottom,#082567_1px,transparent_1px)] bg-[size:70px_70px]" />

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="reveal-up mb-12">
            <p className="text-[#026B07] font-black tracking-[0.2em] uppercase text-xs mb-4">
              Incentive Categories
            </p>

            <h2
              className="text-4xl md:text-5xl font-black text-[#082567] leading-tight max-w-4xl"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              Explore The Benefits Available To Free Zone Enterprises
            </h2>
          </div>

          <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-7">
            <div className="reveal-up bg-white rounded-[30px] border border-slate-200 p-4 shadow-[0_24px_80px_-60px_rgba(8,37,103,0.55)]">
              {incentiveGroups.map((item) => {
                const Icon = item.icon;
                const isActive = active === item.id;

                return (
                  <button
                    key={item.id}
                    onClick={() => setActive(item.id)}
                    className={`w-full flex items-center justify-between gap-4 rounded-[22px] px-5 py-4 text-left transition-all duration-300 ${
                      isActive
                        ? "bg-[#082567] text-white"
                        : "text-[#082567] hover:bg-[#FAFBFC]"
                    }`}
                  >
                    <span className="flex items-center gap-4">
                      <span
                        className={`w-11 h-11 rounded-2xl flex items-center justify-center ${
                          isActive
                            ? "bg-white/10 text-[#F2AF11]"
                            : "bg-[#082567]/5 text-[#082567]"
                        }`}
                      >
                        <Icon size={22} />
                      </span>

                      <span className="font-black text-[15px]">
                        {item.title}
                      </span>
                    </span>

                    <ChevronDown
                      size={18}
                      className={`transition-transform duration-300 ${
                        isActive ? "-rotate-90 text-[#F2AF11]" : ""
                      }`}
                    />
                  </button>
                );
              })}
            </div>

            <div className="reveal-up bg-white rounded-[34px] border border-slate-200 p-7 md:p-8 shadow-[0_26px_90px_-60px_rgba(8,37,103,0.65)]">
              <div className="flex items-start gap-5 mb-7">
                <div className="w-16 h-16 rounded-[22px] bg-[#F2AF11]/15 text-[#082567] border border-[#F2AF11]/25 flex items-center justify-center flex-shrink-0">
                  <CurrentIcon size={30} />
                </div>

                <div>
                  <p className="text-[#026B07] font-black tracking-[0.2em] uppercase text-xs mb-2">
                    Active Category
                  </p>

                  <h3
                    className="text-3xl font-black text-[#082567] leading-tight"
                    style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                  >
                    {current.title}
                  </h3>
                </div>
              </div>

              <div className="space-y-4">
                {current.items.map((item, index) => (
                  <div
                    key={index}
                    className="flex gap-4 border-b border-slate-200 last:border-b-0 pb-4 last:pb-0"
                  >
                    <CheckCircle2
                      size={20}
                      className="text-[#026B07] flex-shrink-0 mt-1"
                    />

                    <p className="text-slate-700 leading-[1.8] text-[15.5px]">
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ALL INCENTIVES SUMMARY */}
      <section className="relative py-24 bg-white overflow-hidden">
        <img
          src={sectionBg}
          alt="Incentives Background"
          className="absolute inset-0 w-full h-full object-cover opacity-[0.05]"
        />
        <div className="absolute inset-0 bg-white/95" />

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="reveal-up flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-12">
            <div>
              <p className="text-[#026B07] font-black tracking-[0.2em] uppercase text-xs mb-4">
                Detailed Summary
              </p>

              <h2
                className="text-4xl md:text-5xl font-black text-[#082567] leading-tight max-w-4xl"
                style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
              >
                Incentives That Improve Cost, Speed & Investor Control
              </h2>
            </div>

            <div className="max-w-md border-l-4 border-[#F2AF11] pl-6">
              <p className="text-slate-600 leading-[1.8] text-[15.5px]">
                These benefits are structured to make Free Zone operations more
                competitive, efficient and attractive to local and international
                investors.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {incentiveGroups.map((group, index) => {
              const Icon = group.icon;

              return (
                <div
                  key={index}
                  className="soft-card bg-white border border-slate-200 rounded-[30px] p-7 hover:-translate-y-2 hover:border-[#F2AF11]/60 hover:shadow-[0_24px_80px_-55px_rgba(8,37,103,0.55)] transition-all duration-500"
                >
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-13 h-13 rounded-2xl bg-[#082567]/5 text-[#082567] border border-[#082567]/10 flex items-center justify-center">
                      <Icon size={24} />
                    </div>

                    <h3
                      className="text-2xl font-black text-[#082567] leading-tight"
                      style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                    >
                      {group.title}
                    </h3>
                  </div>

                  <div className="space-y-3">
                    {group.items.slice(0, 3).map((item, itemIndex) => (
                      <div key={itemIndex} className="flex gap-3">
                        <CheckCircle2
                          size={18}
                          className="text-[#026B07] flex-shrink-0 mt-1"
                        />

                        <p className="text-slate-600 leading-[1.7] text-sm">
                          {item}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* RESOURCE CTA */}
      <section className="py-24 bg-[#FAFBFC]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="reveal-up bg-white rounded-[36px] border border-slate-200 p-8 md:p-12 shadow-[0_24px_80px_-55px_rgba(8,37,103,0.55)]">
            <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-10 items-center">
              <div>
                <div className="w-16 h-16 rounded-[22px] bg-[#F2AF11]/15 text-[#082567] border border-[#F2AF11]/25 flex items-center justify-center mb-7">
                  <FileText size={30} />
                </div>

                <p className="text-[#026B07] font-black tracking-[0.2em] uppercase text-xs mb-4">
                  Investor Resource
                </p>

                <h2
                  className="text-4xl md:text-5xl font-black text-[#082567] leading-tight"
                  style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                >
                  Access Free Zone Incentives And Related Investment Documents
                </h2>
              </div>

              <div>
                <p className="text-slate-600 leading-[1.9] text-[15.5px] mb-7">
                  Investors can review downloadable materials, service charter
                  documents, application forms and regulatory resources to
                  understand the requirements and benefits of operating within
                  OGFZA-regulated Free Zones.
                </p>

                <div className="flex flex-wrap gap-4">
                  <Link
                    to="/publications/ogfza-download"
                    className="inline-flex items-center gap-3 px-7 py-4 rounded-full bg-[#082567] hover:bg-[#F2AF11] hover:text-[#082567] text-white font-black transition-all duration-500"
                  >
                    View Downloads
                    <Download size={20} />
                  </Link>

                  <Link
                    to="/free-zones/registration-procedure"
                    className="inline-flex items-center gap-3 px-7 py-4 rounded-full bg-[#F2AF11] hover:bg-[#082567] text-white font-black transition-all duration-500"
                  >
                    Registration Procedure
                    <ArrowUpRight size={20} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Incentives;